/**
 * APEX v7.7.0 — APEX Score (Climb Readiness)
 *
 * Score 0-100 : à quel point tu es prêt à monter de tier.
 * Sliding window 30 dernières games.
 * 7 axes pondérés comparés à la médiane du tier+1.
 *
 * Voir docs/PRD_OBJECTIVES_V7.7_FINAL.md section 14.
 */

import { isLaneWon } from './analytics.js';

const TIER_ORDER = [
  'IRON', 'BRONZE', 'SILVER', 'GOLD',
  'PLATINUM', 'EMERALD', 'DIAMOND',
  'MASTER', 'GRANDMASTER', 'CHALLENGER'
];

export const APEX_SCORE_AXES = {
  cs_per_min: { label: 'CS/min', weight: 0.20 },
  kda: { label: 'KDA', weight: 0.15 },
  lane_wr_15: { label: 'Lane WR @15', weight: 0.20 },
  damage_per_min: { label: 'Damage/min', weight: 0.15 },
  vision: { label: 'Vision', weight: 0.05 },
  deaths_per_game: { label: 'Deaths/game', weight: 0.10, inverted: true },
  win_rate: { label: 'Win rate', weight: 0.15 }
};

/**
 * Estimations fallback des benchmarks par tier (à remplacer par agrégation users APEX
 * quand la table rank_benchmarks sera peuplée — voir PRD section 14.4).
 *
 * Source : valeurs admises "rule of thumb" pour un ADC. À utiliser comme provisional.
 */
export const TIER_BENCHMARKS_FALLBACK = {
  IRON:        { cs_per_min: { p25: 4.0, p50: 5.0, p75: 5.8 }, kda: { p25: 1.0, p50: 1.5, p75: 2.0 }, lane_wr_15: { p25: 30, p50: 45, p75: 55 }, damage_per_min: { p25: 250, p50: 350, p75: 450 }, vision: { p25: 8, p50: 12, p75: 18 }, deaths_per_game: { p25: 8, p50: 6, p75: 5 }, win_rate: { p25: 40, p50: 50, p75: 55 } },
  BRONZE:      { cs_per_min: { p25: 5.0, p50: 5.8, p75: 6.5 }, kda: { p25: 1.2, p50: 1.8, p75: 2.3 }, lane_wr_15: { p25: 35, p50: 47, p75: 57 }, damage_per_min: { p25: 350, p50: 450, p75: 550 }, vision: { p25: 10, p50: 14, p75: 20 }, deaths_per_game: { p25: 7, p50: 5.5, p75: 4.5 }, win_rate: { p25: 42, p50: 50, p75: 55 } },
  SILVER:      { cs_per_min: { p25: 5.8, p50: 6.5, p75: 7.2 }, kda: { p25: 1.5, p50: 2.0, p75: 2.6 }, lane_wr_15: { p25: 38, p50: 48, p75: 58 }, damage_per_min: { p25: 450, p50: 550, p75: 650 }, vision: { p25: 12, p50: 16, p75: 22 }, deaths_per_game: { p25: 6.5, p50: 5, p75: 4 }, win_rate: { p25: 43, p50: 50, p75: 55 } },
  GOLD:        { cs_per_min: { p25: 6.5, p50: 7.2, p75: 7.8 }, kda: { p25: 1.8, p50: 2.4, p75: 3.0 }, lane_wr_15: { p25: 40, p50: 50, p75: 60 }, damage_per_min: { p25: 550, p50: 650, p75: 750 }, vision: { p25: 14, p50: 18, p75: 24 }, deaths_per_game: { p25: 6, p50: 4.5, p75: 3.5 }, win_rate: { p25: 45, p50: 51, p75: 56 } },
  PLATINUM:    { cs_per_min: { p25: 7.0, p50: 7.6, p75: 8.2 }, kda: { p25: 2.0, p50: 2.7, p75: 3.4 }, lane_wr_15: { p25: 42, p50: 52, p75: 62 }, damage_per_min: { p25: 600, p50: 700, p75: 800 }, vision: { p25: 16, p50: 20, p75: 26 }, deaths_per_game: { p25: 5.5, p50: 4, p75: 3 }, win_rate: { p25: 46, p50: 52, p75: 57 } },
  EMERALD:     { cs_per_min: { p25: 7.4, p50: 8.0, p75: 8.5 }, kda: { p25: 2.2, p50: 3.0, p75: 3.8 }, lane_wr_15: { p25: 44, p50: 53, p75: 63 }, damage_per_min: { p25: 650, p50: 750, p75: 850 }, vision: { p25: 18, p50: 22, p75: 28 }, deaths_per_game: { p25: 5, p50: 3.8, p75: 3 }, win_rate: { p25: 47, p50: 52, p75: 57 } },
  DIAMOND:     { cs_per_min: { p25: 7.8, p50: 8.3, p75: 8.8 }, kda: { p25: 2.5, p50: 3.3, p75: 4.2 }, lane_wr_15: { p25: 46, p50: 55, p75: 65 }, damage_per_min: { p25: 700, p50: 800, p75: 900 }, vision: { p25: 20, p50: 24, p75: 30 }, deaths_per_game: { p25: 4.8, p50: 3.5, p75: 2.8 }, win_rate: { p25: 48, p50: 53, p75: 58 } },
  MASTER:      { cs_per_min: { p25: 8.0, p50: 8.6, p75: 9.1 }, kda: { p25: 2.8, p50: 3.7, p75: 4.7 }, lane_wr_15: { p25: 48, p50: 57, p75: 66 }, damage_per_min: { p25: 750, p50: 850, p75: 950 }, vision: { p25: 22, p50: 26, p75: 32 }, deaths_per_game: { p25: 4.5, p50: 3.3, p75: 2.5 }, win_rate: { p25: 50, p50: 54, p75: 59 } },
  GRANDMASTER: { cs_per_min: { p25: 8.4, p50: 8.9, p75: 9.4 }, kda: { p25: 3.0, p50: 4.0, p75: 5.2 }, lane_wr_15: { p25: 50, p50: 58, p75: 68 }, damage_per_min: { p25: 800, p50: 900, p75: 1000 }, vision: { p25: 24, p50: 28, p75: 34 }, deaths_per_game: { p25: 4.2, p50: 3.0, p75: 2.3 }, win_rate: { p25: 51, p50: 55, p75: 60 } },
  CHALLENGER:  { cs_per_min: { p25: 8.6, p50: 9.2, p75: 9.7 }, kda: { p25: 3.3, p50: 4.4, p75: 5.7 }, lane_wr_15: { p25: 52, p50: 60, p75: 70 }, damage_per_min: { p25: 850, p50: 950, p75: 1050 }, vision: { p25: 26, p50: 30, p75: 36 }, deaths_per_game: { p25: 4.0, p50: 2.8, p75: 2.0 }, win_rate: { p25: 52, p50: 56, p75: 61 } }
};

function isCancelled(game) {
  if (!game) return false;
  if (game.isRemake === true) return true;
  if (typeof game.duration === 'number' && game.duration > 0 && game.duration < 5) return true;
  return false;
}

function nextTier(currentTier) {
  if (!currentTier) return 'SILVER';
  const idx = TIER_ORDER.indexOf(currentTier);
  if (idx === -1) return 'MASTER';
  if (idx === TIER_ORDER.length - 1) return null; // Challenger
  return TIER_ORDER[idx + 1];
}

/**
 * Score d'un axe : 0-100.
 *   - yourValue <= p25 → 0
 *   - yourValue >= p75 → 100
 *   - entre p25 et p50 → 0-50 linéaire
 *   - entre p50 et p75 → 50-100 linéaire
 */
export function computeAxisScore(yourValue, benchmark) {
  if (!benchmark || typeof yourValue !== 'number' || isNaN(yourValue)) return 50;
  const { p25, p50, p75 } = benchmark;
  if (typeof p25 !== 'number' || typeof p50 !== 'number' || typeof p75 !== 'number') return 50;

  // v7.8.6 : sous p25, on ne tombe plus brutalement à 0 (trompeur : donnait
  // l'impression que l'axe n'était "pas calculé"). Dégradé doux 0→25 sur la
  // plage [p25*0.7, p25], plancher à 0 seulement très en dessous.
  if (yourValue >= p75) return 100;
  if (yourValue <= p25) {
    const floor = p25 * 0.7;
    if (yourValue <= floor) return 0;
    // 0 → 25 entre floor et p25
    return Math.round(25 * (yourValue - floor) / (p25 - floor));
  }
  if (yourValue <= p50) return Math.round(25 + 25 * (yourValue - p25) / (p50 - p25));
  return Math.round(50 + 50 * (yourValue - p50) / (p75 - p50));
}

/**
 * Score inversé (low = good) : deaths/game.
 *   - yourValue <= p75 → 100 (deaths bas = bon)
 *   - yourValue >= p25 → 0
 */
export function computeAxisScoreInverted(yourValue, benchmark) {
  if (!benchmark || typeof yourValue !== 'number' || isNaN(yourValue)) return 50;
  const { p25, p50, p75 } = benchmark;
  if (yourValue >= p25) return 0;
  if (yourValue <= p75) return 100;
  if (yourValue >= p50) return Math.round(50 * (p25 - yourValue) / (p25 - p50));
  return Math.round(50 + 50 * (p50 - yourValue) / (p50 - p75));
}

function computeKda(game) {
  if (!game) return 0;
  const d = game.deaths || 0;
  if (d === 0) return ((game.kills || 0) + (game.assists || 0)) * 1.5;
  return ((game.kills || 0) + (game.assists || 0)) / d;
}

// isLaneWon est défini dans analytics.js — importé ci-dessus pour éviter la duplication

/**
 * Calcule le APEX Score sur les 30 dernières games.
 * Retourne { score, axes, status, message }.
 */
export function computeApexScore(allGames, userSettings, benchmarks = TIER_BENCHMARKS_FALLBACK) {
  // isFallback = true seulement si on utilise le fallback hardcodé
  const usingFallback = benchmarks === TIER_BENCHMARKS_FALLBACK;
  if (!Array.isArray(allGames) || allGames.length === 0) {
    return {
      score: null,
      axes: null,
      status: 'no_data',
      message: 'Pas encore de games pour calculer ton score',
      sampleSize: 0,
      targetTier: null,
      isFallback: true
    };
  }

  // Filtre + tri + 30 dernières
  // v8.22 : on n'inclut que les games jouées sur le rôle principal
  const primaryRole = (userSettings?.primary_role || 'ADC').toUpperCase();
  // Mapping userSettings.primary_role → game.role (tel que parsé par riot.js)
  const ROLE_TO_GAME_ROLE = {
    ADC:     'ADC',
    MID:     'MID',
    TOP:     'TOP',
    JUNGLE:  'JG',
    SUPPORT: 'SUP',
    // Aliases
    JG:      'JG',
    SUP:     'SUP',
  };
  const expectedGameRole = ROLE_TO_GAME_ROLE[primaryRole] || 'ADC';

  const valid = allGames
    .filter(g => {
      if (isCancelled(g)) return false;
      // Exclure les games sur rôle secondaire et autofill
      // game.role est dérivé de teamPosition Riot ('ADC', 'MID', 'TOP', 'JG', 'SUP')
      if (g.role && g.role !== expectedGameRole) return false;
      return true;
    })
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 30);

  if (valid.length === 0) {
    return {
      score: null,
      axes: null,
      status: 'no_data',
      message: 'Aucune game valide récente',
      sampleSize: 0,
      targetTier: null,
      isFallback: true
    };
  }

  const currentTier = userSettings?.current_tier || 'GOLD';
  const targetTier = nextTier(currentTier);

  // v7.8.7 : retour à la comparaison TIER+1 ("Climb Readiness : es-tu prêt
  // à monter ?") — c'est le concept voulu du produit. Le dégradé doux sous
  // p25 (computeAxisScore v7.8.6) évite que les scores soient écrasés à 0.
  // Cas Challenger : pas de tier+1 → on compare au tier actuel ("mastery").
  const benchmarkTier = targetTier || currentTier;
  const benchmark = benchmarks[benchmarkTier];
  if (!benchmark) {
    return {
      score: null,
      axes: null,
      status: 'no_benchmark',
      message: `Pas de benchmark dispo pour ${benchmarkTier}`,
      sampleSize: valid.length,
      targetTier,
      isFallback: true
    };
  }

  const totalDur = valid.reduce((s, g) => s + (g.duration || 0), 0); // duration en MINUTES

  const csPerMinValues = valid.map(g => g.csPerMin || 0).filter(v => v > 0);
  const avgCsPerMin = csPerMinValues.length > 0
    ? csPerMinValues.reduce((s, v) => s + v, 0) / csPerMinValues.length
    : 0;

  const avgKda = valid.reduce((s, g) => s + computeKda(g), 0) / valid.length;

  const validLane = valid.filter(g => g.timeFrames?.min15);
  const laneWr15 = validLane.length > 0
    ? Math.round((validLane.reduce((acc, g) => {
      const r = isLaneWon(g, 15, userSettings?.primary_role || 'ADC');
      return acc + (r === 'won' ? 1 : r === 'drawn' ? 0.5 : 0);
    }, 0) / validLane.length) * 100)
    : 50;

  // damageDealt = total dégâts champions ; on dérive le /min via duration (minutes)
  const dmgPerMinValues = valid
    .map(g => {
      const dmg = g.damageDealt || 0;
      const dur = g.duration || 0; // minutes
      return dur > 0 ? dmg / dur : 0;
    })
    .filter(v => v > 0);
  const avgDmgPerMin = dmgPerMinValues.length > 0
    ? dmgPerMinValues.reduce((s, v) => s + v, 0) / dmgPerMinValues.length
    : 0;

  const visionValues = valid
    .map(g => g.myStats?.visionScore ?? g.visionScore ?? 0)
    .filter(v => v > 0);
  const avgVision = visionValues.length > 0
    ? visionValues.reduce((s, v) => s + v, 0) / visionValues.length
    : 0;

  const avgDeaths = valid.reduce((s, g) => s + (g.deaths || 0), 0) / valid.length;

  const wins = valid.filter(g => g.result === 'W').length;
  const winRate = Math.round((wins / valid.length) * 100);

  // Score par axe
  const axes = {
    cs_per_min: { value: avgCsPerMin, score: computeAxisScore(avgCsPerMin, benchmark.cs_per_min) },
    kda: { value: avgKda, score: computeAxisScore(avgKda, benchmark.kda) },
    lane_wr_15: { value: laneWr15, score: computeAxisScore(laneWr15, benchmark.lane_wr_15) },
    damage_per_min: { value: avgDmgPerMin, score: computeAxisScore(avgDmgPerMin, benchmark.damage_per_min) },
    vision: { value: avgVision, score: computeAxisScore(avgVision, benchmark.vision) },
    deaths_per_game: { value: avgDeaths, score: computeAxisScoreInverted(avgDeaths, benchmark.deaths_per_game) },
    win_rate: { value: winRate, score: computeAxisScore(winRate, benchmark.win_rate) }
  };

  // Moyenne pondérée
  let total = 0;
  for (const [key, axis] of Object.entries(axes)) {
    total += axis.score * APEX_SCORE_AXES[key].weight;
  }
  const score = Math.round(total);

  // Interprétation
  let status, message;
  if (targetTier === null) {
    status = 'challenger_mastery';
    message = score >= 75
      ? 'Tu domines Challenger 🔥'
      : 'Maintiens ton niveau Challenger';
  } else if (score < 40) {
    status = 'below_level';
    message = `Pas encore le niveau ${benchmarkTier}`;
  } else if (score < 65) {
    status = 'solid';
    message = `En route vers ${benchmarkTier}`;
  } else if (score < 85) {
    status = 'ready';
    message = `Prêt pour ${benchmarkTier}, ça va venir`;
  } else {
    status = 'overperform';
    message = `Niveau ${benchmarkTier} déjà atteint 🔥`;
  }

  // Expose les valeurs cibles p50 pour l'affichage dans CoachingTab
  const benchmarkTargets = {
    cs_per_min:     benchmark.cs_per_min.p50,
    kda:            benchmark.kda.p50,
    lane_wr_15:     benchmark.lane_wr_15.p50,
    damage_per_min: benchmark.damage_per_min.p50,
    vision:         benchmark.vision.p50,
    deaths_per_game:benchmark.deaths_per_game.p50,
    win_rate:       benchmark.win_rate.p50,
  };

  return {
    score,
    axes,
    status,
    message,
    sampleSize: valid.length,
    targetTier: benchmarkTier,
    benchmarkTargets,
    isFallback: usingFallback
  };
}

/**
 * Couleur selon le statut (cohérent avec le reste de l'app).
 */
export function getApexScoreColor(status) {
  switch (status) {
    case 'below_level':       return { text: 'text-rose-400', bg: 'bg-rose-400', border: 'border-rose-400/60', glow: 'rgba(244, 63, 94, 0.4)', hex: '#fb7185' };
    case 'solid':             return { text: 'text-amber-400', bg: 'bg-amber-400', border: 'border-amber-400/60', glow: 'rgba(251, 191, 36, 0.4)', hex: '#fbbf24' };
    case 'ready':             return { text: 'text-cyan-400', bg: 'bg-cyan-400', border: 'border-cyan-400/60', glow: 'rgba(34, 211, 238, 0.4)', hex: '#22d3ee' };
    case 'overperform':       return { text: 'text-emerald-400', bg: 'bg-emerald-400', border: 'border-emerald-400/60', glow: 'rgba(52, 211, 153, 0.5)', hex: '#34d399' };
    case 'challenger_mastery':return { text: 'text-fuchsia-400', bg: 'bg-fuchsia-400', border: 'border-fuchsia-400/60', glow: 'rgba(232, 121, 249, 0.5)', hex: '#e879f9' };
    default:                  return { text: 'text-zinc-400', bg: 'bg-zinc-400', border: 'border-zinc-700', glow: 'rgba(161, 161, 170, 0.3)', hex: '#a1a1aa' };
  }
}
